"use client";

import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  fadeDirection: number;
  color: string;
}

export function GlitterParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener("resize", setCanvasSize);

    // Particle colors (purple/pink/white theme)
    const colors = [
      "rgba(196, 181, 253, ", // purple-300
      "rgba(167, 139, 250, ", // purple-400
      "rgba(244, 114, 182, ", // pink-400
      "rgba(251, 207, 232, ", // pink-200
      "rgba(255, 255, 255, ", // white
    ];

    // Create particles (reduced 15% for performance)
    const particleCount = 68;
    const particles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2.5 + 0.5, // 0.5 to 3px
        speedX: (Math.random() - 0.5) * 0.6, // 2x for 30fps
        speedY: (Math.random() - 0.5) * 0.6, // 2x for 30fps
        opacity: Math.random() * 0.5 + 0.3, // 0.3 to 0.8
        fadeDirection: Math.random() > 0.5 ? 1 : -1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    // Animation loop at 30 FPS (skip every other frame)
    let animationFrameId: number;
    let frameCount = 0;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      
      // Skip every other frame for 30fps
      frameCount++;
      if (frameCount % 2 !== 0) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Twinkle effect (2x for 30fps)
        particle.opacity += particle.fadeDirection * 0.02;
        if (particle.opacity >= 0.9 || particle.opacity <= 0.2) {
          particle.fadeDirection *= -1;
        }

        // Draw particle with glow
        ctx.shadowBlur = 8;
        ctx.shadowColor = particle.color + particle.opacity + ")";
        ctx.fillStyle = particle.color + particle.opacity + ")";
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener("resize", setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
